/**
 * Repair Logs Utility
 * Manages repair session logs in localStorage
 */

const STORAGE_KEY = 'fixit_repair_logs';

/**
 * Generate a unique ID for a log entry
 */
const generateId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Save a completed repair log
 * @param {Object} logData - The repair session data
 * @returns {string} The ID of the saved log
 */
export const saveRepairLog = (logData) => {
    try {
        const logs = getRepairLogs();
        const newLog = {
            id: generateId(),
            timestamp: new Date().toISOString(),
            ...logData,
            completedAt: new Date().toISOString(),
        };

        logs.unshift(newLog); // Add to beginning (most recent first)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));
        return newLog.id;
    } catch (error) {
        console.error('Error saving repair log:', error);
        return null;
    }
};

/**
 * Get all repair logs
 * @returns {Array} Array of repair logs sorted by date (newest first)
 */
export const getRepairLogs = () => {
    try {
        const logs = localStorage.getItem(STORAGE_KEY);
        return logs ? JSON.parse(logs) : [];
    } catch (error) {
        console.error('Error reading repair logs:', error);
        return [];
    }
};

/**
 * Get a single repair log by ID
 * @param {string} id - The log ID
 * @returns {Object|null} The log object or null if not found
 */
export const getRepairLog = (id) => {
    try {
        const logs = getRepairLogs();
        return logs.find(log => log.id === id) || null;
    } catch (error) {
        console.error('Error reading repair log:', error);
        return null;
    }
};

/**
 * Delete a repair log by ID
 * @param {string} id - The log ID to delete
 * @returns {boolean} True if deleted successfully
 */
export const deleteRepairLog = (id) => {
    try {
        const logs = getRepairLogs();
        const filteredLogs = logs.filter(log => log.id !== id);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredLogs));
        return true;
    } catch (error) {
        console.error('Error deleting repair log:', error);
        return false;
    }
};

/**
 * Clear all repair logs
 * @returns {boolean} True if cleared successfully
 */
export const clearAllLogs = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        return true;
    } catch (error) {
        console.error('Error clearing repair logs:', error);
        return false;
    }
};

/**
 * Get logs count
 * @returns {number} Number of stored logs
 */
export const getLogsCount = () => {
    try {
        const logs = getRepairLogs();
        return logs.length;
    } catch (error) {
        return 0;
    }
};
